/**
 * Reads the Cognito id_token that react-oidc-context parks in localStorage
 * (`userStore` in `createCognitoAuthConfig`), shared by every tab.
 *
 * The JSON.parse is guarded: a malformed `oidc.user:*` entry used to throw
 * inside the base query and surface as an opaque unhandled rejection.
 */
export const createCognitoTokenReader =
  (authority: string, clientId: string) =>
  (): string => {
    const raw = localStorage.getItem(`oidc.user:${authority}:${clientId}`);
    if (!raw) return '';
    try {
      return (JSON.parse(raw) as { id_token?: string }).id_token ?? '';
    } catch {
      return '';
    }
  };

/**
 * Treat a token this close to its `exp` as already gone, so we don't race the
 * backend into rejecting a request we thought was still valid.
 */
const EXPIRY_SKEW_SECONDS = 30;

const decodeJwtPayload = (token: string): { exp?: number } | null => {
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
};

/**
 * Whether the session behind a request is actually gone.
 *
 * This is the discriminator for 403s. The backend (Django REST Framework)
 * answers 403 both for an unauthenticated request *and* for a signed-in user
 * who lacks permission, so the status alone cannot tell the two apart — only
 * the token can.
 *
 * Unreadable tokens deliberately return `false`: a wrong "expired" verdict
 * throws the user out of a perfectly good session, which is the worse failure.
 * A missing token is unambiguous, so that one returns `true`.
 */
export const isSessionTokenExpired = (token: string | undefined): boolean => {
  if (!token) return true;

  const exp = decodeJwtPayload(token)?.exp;
  if (typeof exp !== 'number') return false;

  return Date.now() >= (exp - EXPIRY_SKEW_SECONDS) * 1000;
};

/**
 * Drops every `oidc.user:*` entry react-oidc-context may hold — in
 * localStorage, where it keeps them, and in sessionStorage, where it kept them
 * before sessions were shared across tabs.
 *
 * Note this only clears *storage*. The UserManager keeps the user in memory
 * with an `automaticSilentRenew` timer already armed, so on its own this gets
 * undone by the next silent renew — the listener also has to call
 * `auth.removeUser()`. See `Authenticate`.
 */
export const clearStoredSession = (): void => {
  [globalThis.localStorage, globalThis.sessionStorage].forEach((storage) => {
    if (!storage) return;

    Object.keys(storage)
      .filter((key) => key.startsWith('oidc.user:'))
      .forEach((key) => storage.removeItem(key));
  });
};

/** Event the app listens for to surface the "session expired" screen. */
export const TOKEN_EXPIRED_EVENT = 'tokenExpired';
